<template>
  <Modal ref="modal" @show="cargar">
    <h2 class="box__title">Dependencia de fecha · {{ table.name }}</h2>
    <p class="extras-mg-deps__nota">
      Di qué filas dependen de otras. Si una termina más tarde, las que dependen
      de ella se corren solas, y las que dependen de esas también, como en
      Monday.
    </p>

    <Error :error="error"></Error>
    <div v-if="cargando" class="loading"></div>

    <form v-else class="extras-mg-deps__form" @submit.prevent="guardar">
      <!-- La regla sigue guardada, pero el backend la hace a un lado mientras
           una columna esté rota (rule_types._en_uso). Sin esto el modal se
           veía normal y nadie sabía por qué las fechas dejaron de correr. -->
      <Alert v-if="enPausa" type="warning" class="extras-mg-deps__pausa">
        <template #title>La dependencia está en pausa</template>
        {{
          regla.error_text ||
          'Una de sus columnas se borró o cambió de tipo: regrésala como estaba o elige otra.'
        }}
        Mientras siga así, no corre fechas ni marca filas.
      </Alert>

      <FormGroup label="Activa" small-label>
        <Checkbox v-model="valores.is_active">
          Correr las fechas automáticamente en esta tabla
        </Checkbox>
      </FormGroup>

      <div class="extras-mg-deps__fila">
        <FormGroup label="Inicio" small-label required>
          <Dropdown v-model="valores.campo_inicio_id" :show-search="false">
            <DropdownItem
              v-for="f in fechas"
              :key="f.id"
              :name="f.name"
              :value="f.id"
            ></DropdownItem>
          </Dropdown>
        </FormGroup>
        <FormGroup label="Fin" small-label required>
          <Dropdown v-model="valores.campo_fin_id" :show-search="false">
            <DropdownItem
              v-for="f in fechas"
              :key="f.id"
              :name="f.name"
              :value="f.id"
            ></DropdownItem>
          </Dropdown>
        </FormGroup>
      </div>

      <div class="extras-mg-deps__fila">
        <FormGroup label="Duración (días)" small-label>
          <Dropdown
            v-model="valores.campo_duracion_id"
            :show-search="false"
          >
            <DropdownItem name="Sin columna de duración" :value="null"></DropdownItem>
            <DropdownItem
              v-for="f in numeros"
              :key="f.id"
              :name="f.name"
              :value="f.id"
            ></DropdownItem>
          </Dropdown>
        </FormGroup>
        <FormGroup label="Predecesores" small-label required>
          <Dropdown v-model="valores.campo_predecesores_id" :show-search="false">
            <DropdownItem
              v-for="f in enlaces"
              :key="f.id"
              :name="f.name"
              :value="f.id"
            ></DropdownItem>
          </Dropdown>
        </FormGroup>
      </div>

      <p v-if="faltan.length" class="extras-mg-deps__faltan">
        Faltan columnas: {{ faltan.join(', ') }}.
        <ButtonText :loading="creando" @click="crearQueFaltan">
          Crearlas
        </ButtonText>
      </p>

      <div class="extras-mg-deps__fila">
        <FormGroup label="Al correr fechas" small-label>
          <Dropdown v-model="valores.modo" :show-search="false">
            <DropdownItem
              name="Flexible"
              value="flexible"
              description="Solo mueve una tarea si su predecesora ahora la alcanza."
            ></DropdownItem>
            <DropdownItem
              name="Estricto"
              value="estricto"
              description="La tarea siempre empieza justo después de su predecesora."
            ></DropdownItem>
          </Dropdown>
        </FormGroup>
        <FormGroup label="Días de holgura" small-label>
          <FormInput v-model="valores.holgura" type="number" :min="0" />
        </FormGroup>
      </div>

      <FormGroup small-label>
        <Checkbox v-model="valores.incluir_fines">
          Contar sábados y domingos
        </Checkbox>
      </FormGroup>

      <div class="extras-mg-deps__acciones">
        <ButtonText
          v-if="regla"
          type="secondary"
          icon="iconoir-bin"
          @click="quitar"
          >Quitar la dependencia</ButtonText
        >
        <Button type="primary" :loading="guardando" :disabled="guardando">
          Guardar
        </Button>
      </div>
    </form>
  </Modal>
</template>

<script>
/**
 * "Dependencia de fecha" de una tabla (extras_mg/fechas). Es una regla de campo
 * del core: se guarda por su API y el core la aplica en cada cambio.
 */
import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import FieldService from '@baserow/modules/database/services/field'
import FieldRulesService from '@baserow/modules/database/services/fieldRules'

const TIPO = 'dependencia_fechas_mg'

export default {
  name: 'DependenciaFechasModal',
  mixins: [modal, error],
  props: {
    table: { type: Object, required: true },
  },
  data() {
    return {
      cargando: false,
      guardando: false,
      creando: false,
      campos: [],
      regla: null,
      valores: this.porDefecto(),
    }
  },
  computed: {
    fechas() {
      // Sin hora: con hora, el día depende de la zona y el backend la rechaza.
      return this.campos.filter(
        (f) => f.type === 'date' && !f.date_include_time
      )
    },
    numeros() {
      return this.campos.filter((f) => f.type === 'number')
    },
    enlaces() {
      return this.campos.filter(
        (f) => f.type === 'link_row' && f.link_row_table_id === this.table.id
      )
    },
    enPausa() {
      // Apagada no se revisa (el core solo revisa las prendidas): su is_valid
      // puede ser viejo. Al prenderla, guardar ya dice qué columna falla.
      return !!(this.regla && this.regla.is_active && !this.regla.is_valid)
    },
    faltan() {
      const faltan = []
      if (this.fechas.length < 2) {
        faltan.push('dos de fecha sin hora (Inicio y Fin)')
      }
      if (!this.enlaces.length) faltan.push('Predecesores (enlace a esta tabla)')
      return faltan
    },
  },
  methods: {
    porDefecto() {
      return {
        is_active: true,
        campo_inicio_id: null,
        campo_fin_id: null,
        campo_duracion_id: null,
        campo_predecesores_id: null,
        modo: 'flexible',
        holgura: 0,
        incluir_fines: true,
      }
    },
    async cargar() {
      this.cargando = true
      this.hideError()
      try {
        const [{ data: campos }, { data: reglas }] = await Promise.all([
          FieldService(this.$client).fetchAll(this.table.id),
          FieldRulesService(this.$client).getRules(this.table.id),
        ])
        this.campos = campos
        this.regla = reglas.find((r) => r.type === TIPO) || null
        this.valores = this.regla
          ? { ...this.porDefecto(), ...this.regla }
          : this.adivinar()
      } catch (e) {
        this.mostrarError(e)
      }
      this.cargando = false
    },
    /** Sin regla todavía: se proponen las columnas por su nombre. */
    adivinar() {
      const v = this.porDefecto()
      const por = (lista, ...palabras) =>
        lista.find((f) =>
          palabras.some((p) => f.name.toLowerCase().includes(p))
        )?.id ?? null
      v.campo_inicio_id = por(this.fechas, 'inicio', 'start', 'desde')
      v.campo_fin_id = por(this.fechas, 'fin', 'end', 'hasta', 'entrega')
      v.campo_duracion_id = por(this.numeros, 'duraci', 'días', 'dias')
      v.campo_predecesores_id = por(this.enlaces, 'predece', 'depend') ||
        this.enlaces[0]?.id || null
      return v
    },
    async crearQueFaltan() {
      this.creando = true
      this.hideError()
      try {
        const servicio = FieldService(this.$client)
        const abierta = this.$store.getters['table/getSelected']
        const nuevo = async (valores) => {
          const { data } = await servicio.create(this.table.id, valores)
          this.campos.push(data)
          // Si es la tabla que se está viendo, que la columna aparezca ya: el
          // aviso en vivo del servidor no le llega a quien la creó.
          if (abierta && abierta.id === this.table.id) {
            const { related_fields: relacionados = [], ...campo } = data
            await this.$store.dispatch('field/forceCreate', {
              table: abierta,
              values: campo,
              relatedFields: relacionados,
            })
          }
          return data.id
        }
        if (!this.valores.campo_inicio_id) {
          this.valores.campo_inicio_id = await nuevo({ name: 'Inicio', type: 'date', date_format: 'EU' })
        }
        if (!this.valores.campo_fin_id) {
          this.valores.campo_fin_id = await nuevo({ name: 'Fin', type: 'date', date_format: 'EU' })
        }
        if (!this.valores.campo_duracion_id) {
          this.valores.campo_duracion_id = await nuevo({
            name: 'Duración (días)',
            type: 'number',
            number_decimal_places: 0,
          })
        }
        if (!this.valores.campo_predecesores_id) {
          this.valores.campo_predecesores_id = await nuevo({
            name: 'Predecesores',
            type: 'link_row',
            link_row_table_id: this.table.id,
            has_related_field: false,
          })
        }
      } catch (e) {
        this.mostrarError(e)
      }
      this.creando = false
    },
    async guardar() {
      this.guardando = true
      this.hideError()
      const valores = {
        ...this.valores,
        type: TIPO,
        holgura: Number(this.valores.holgura) || 0,
      }
      try {
        const servicio = FieldRulesService(this.$client)
        const { data } = this.regla
          ? await servicio.updateRule(this.table.id, this.regla.id, valores)
          : await servicio.createRule(this.table.id, valores)
        this.regla = data
        // Que la tabla abierta sepa de la regla (filas inválidas, etc.).
        await this.$store.dispatch('fieldRules/fetchInitial', {
          tableId: this.table.id,
        })
        this.$store.dispatch('toast/success', {
          title: 'Dependencia de fecha guardada',
          message: data.is_active
            ? 'Las fechas se correrán solas al cambiar una tarea.'
            : 'Queda guardada pero apagada.',
        })
        this.hide()
      } catch (e) {
        this.mostrarError(e)
      }
      this.guardando = false
    },
    async quitar() {
      this.hideError()
      try {
        await FieldRulesService(this.$client).deleteRule(
          this.table.id,
          this.regla.id
        )
        this.regla = null
        await this.$store.dispatch('fieldRules/fetchInitial', {
          tableId: this.table.id,
        })
        this.hide()
      } catch (e) {
        this.mostrarError(e)
      }
    },
    mostrarError(e) {
      const datos = e?.response?.data
      const detalle = datos?.detail
      if (detalle && typeof detalle === 'object') {
        e.handler?.handled?.()
        const texto = Object.values(detalle)
          .flat()
          .map((d) => (typeof d === 'string' ? d : d?.error || JSON.stringify(d)))
          .join(' ')
        this.showError('Revisa las columnas', texto)
      } else if (typeof detalle === 'string') {
        e.handler?.handled?.()
        this.showError('No se pudo', detalle)
      } else {
        this.handleError(e)
      }
    },
  },
}
</script>

<style lang="scss">
/* Sin colores fijos: build-theme.py invierte cualquier hex del CSS compilado. */
.extras-mg-deps__nota {
  font-size: 13px;
  margin: 0 0 16px;
  opacity: 0.7;
}

.extras-mg-deps__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.extras-mg-deps__fila {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
}

.extras-mg-deps__faltan {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 8px;
  margin: 0;
}

.extras-mg-deps__acciones {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;

  > .button:only-child {
    margin-left: auto;
  }
}
</style>
